import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../App';
import { toast } from 'sonner';
import SuperAdminLayout from '../../components/SuperAdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Users,
  Plus,
  Upload,
  Pencil,
  Trash2,
  MoreHorizontal,
  RefreshCw,
  Search,
  Image,
  Download,
  Globe,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuperAdminDefaultContestants = () => {
  const { authAxios } = useContext(AppContext);
  
  const [contestants, setContestants] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Filters
  const [seasonFilter, setSeasonFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [showCountriesDialog, setShowCountriesDialog] = useState(false);
  
  const [editingContestant, setEditingContestant] = useState(null);
  const [deletingContestant, setDeletingContestant] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    season_number: '',
    country_id: '',
    age: '',
    hometown: '',
    photo_url: '',
    photo_type: 'url'
  });
  
  // Bulk upload state
  const [bulkSeasonNumber, setBulkSeasonNumber] = useState('');
  const [bulkCountryId, setBulkCountryId] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  
  // Country management
  const [newCountry, setNewCountry] = useState({ name: '', code: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contestantsRes, countriesRes] = await Promise.all([
        authAxios.get(`${API}/platform/default-contestants`),
        authAxios.get(`${API}/platform/countries`)
      ]);
      setContestants(contestantsRes.data);
      setCountries(countriesRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Get unique season numbers from contestants
  const uniqueSeasons = [...new Set(contestants.map(c => c.season_number))].sort((a, b) => b - a);

  // Filter contestants
  const filteredContestants = contestants.filter(c => {
    const matchesSearch = !searchQuery || 
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.hometown?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSeason = seasonFilter === 'all' || c.season_number === parseInt(seasonFilter);
    const matchesCountry = countryFilter === 'all' || c.country_id === countryFilter;
    return matchesSearch && matchesSeason && matchesCountry;
  });

  const resetForm = () => {
    setFormData({
      name: '',
      season_number: '',
      country_id: '',
      age: '',
      hometown: '',
      photo_url: '',
      photo_type: 'url'
    });
  };

  const handleAdd = async () => {
    if (!formData.name || !formData.season_number || !formData.country_id) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setProcessing(true);
    try {
      await authAxios.post(`${API}/platform/default-contestants`, {
        ...formData,
        season_number: parseInt(formData.season_number),
        age: formData.age ? parseInt(formData.age) : null
      });
      toast.success('Contestant added');
      setShowAddDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add contestant');
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = async () => {
    if (!formData.name) {
      toast.error('Name is required');
      return;
    }
    
    setProcessing(true);
    try {
      await authAxios.patch(`${API}/platform/default-contestants/${editingContestant.id}`, {
        ...formData,
        season_number: parseInt(formData.season_number),
        age: formData.age ? parseInt(formData.age) : null
      });
      toast.success('Contestant updated');
      setShowEditDialog(false);
      setEditingContestant(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update contestant');
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    setProcessing(true);
    try {
      await authAxios.delete(`${API}/platform/default-contestants/${deletingContestant.id}`);
      toast.success('Contestant deleted');
      setShowDeleteDialog(false);
      setDeletingContestant(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contestant');
    } finally {
      setProcessing(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile || !bulkSeasonNumber || !bulkCountryId) {
      toast.error('Please select a file, season number, and country');
      return;
    }
    
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      formData.append('season_number', bulkSeasonNumber);
      formData.append('country_id', bulkCountryId);
      
      const response = await authAxios.post(`${API}/platform/default-contestants/bulk`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Uploaded ${response.data.created_count} contestants`);
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} rows had errors`);
      }
      setShowBulkUploadDialog(false);
      setBulkFile(null);
      setBulkSeasonNumber('');
      setBulkCountryId('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setProcessing(false);
    }
  };

  const handleAddCountry = async () => {
    if (!newCountry.name || !newCountry.code) {
      toast.error('Please fill in country name and code');
      return;
    }
    
    try {
      await authAxios.post(`${API}/platform/countries`, newCountry);
      toast.success('Country added');
      setNewCountry({ name: '', code: '' });
      const res = await authAxios.get(`${API}/platform/countries`);
      setCountries(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add country');
    }
  };

  const handleDeleteCountry = async (countryId) => {
    try {
      await authAxios.delete(`${API}/platform/countries/${countryId}`);
      toast.success('Country deleted');
      const res = await authAxios.get(`${API}/platform/countries`);
      setCountries(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete country');
    }
  };

  const openEditDialog = (contestant) => {
    setEditingContestant(contestant);
    setFormData({
      name: contestant.name || '',
      season_number: contestant.season_number?.toString() || '',
      country_id: contestant.country_id || '',
      age: contestant.age?.toString() || '',
      hometown: contestant.hometown || '',
      photo_url: contestant.photo_url || '',
      photo_type: contestant.photo_type || 'url'
    });
    setShowEditDialog(true);
  };

  const downloadTemplate = () => {
    const csv = 'name,age,hometown,photo_url\nJohn Doe,32,New York,https://example.com/photo.jpg\nJane Smith,28,Los Angeles,';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contestants_template.csv';
    a.click();
  };

  return (
    <SuperAdminLayout title="Default Contestants" onRefresh={loadData}>
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-white">Default Contestants</h1>
            <p className="text-sm text-zinc-400">
              {contestants.length} contestants • {uniqueSeasons.length} seasons • {countries.length} countries
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCountriesDialog(true)}
              className="border-zinc-700 text-zinc-300"
            >
              <Globe className="w-4 h-4 mr-2" />
              Manage Countries
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkUploadDialog(true)}
              className="border-zinc-700 text-zinc-300"
            >
              <Upload className="w-4 h-4 mr-2" />
              Bulk Upload
            </Button>
            <Button
              size="sm"
              onClick={() => { resetForm(); setShowAddDialog(true); }}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Contestant
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search contestants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-700 text-white"
            />
          </div>
          <Select value={seasonFilter} onValueChange={setSeasonFilter}>
            <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-700 text-white">
              <SelectValue placeholder="Season" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all">All Seasons</SelectItem>
              {uniqueSeasons.map(s => (
                <SelectItem key={s} value={s.toString()}>Season {s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-700 text-white">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700">
              <SelectItem value="all">All Countries</SelectItem>
              {countries.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-zinc-800/50">
                <TableHead className="text-zinc-400 w-16">Photo</TableHead>
                <TableHead className="text-zinc-400">Name</TableHead>
                <TableHead className="text-zinc-400">Season</TableHead>
                <TableHead className="text-zinc-400">Country</TableHead>
                <TableHead className="text-zinc-400">Age</TableHead>
                <TableHead className="text-zinc-400">Hometown</TableHead>
                <TableHead className="text-zinc-400 w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-zinc-500 mx-auto" />
                  </TableCell>
                </TableRow>
              ) : filteredContestants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                    No contestants found
                  </TableCell>
                </TableRow>
              ) : (
                filteredContestants.map((contestant) => (
                  <TableRow key={contestant.id} className="border-zinc-800 hover:bg-zinc-800/50">
                    <TableCell>
                      <img
                        src={contestant.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(contestant.name)}&background=3f3f46&color=fff&size=40`}
                        alt={contestant.name}
                        className="w-10 h-10 rounded-full object-cover"
                        onError={(e) => { 
                          const fallback = `https://ui-avatars.com/api/?name=${encodeURIComponent(contestant.name)}&background=3f3f46&color=fff&size=40`;
                          if (e.target.src !== fallback) {
                            e.target.onerror = null;
                            e.target.src = fallback;
                          }
                        }}
                        referrerPolicy="no-referrer"
                      />
                    </TableCell>
                    <TableCell className="text-white font-medium">{contestant.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="border-zinc-600 text-zinc-300">
                        Season {contestant.season_number}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-300">{contestant.country?.name || '-'}</TableCell>
                    <TableCell className="text-zinc-300">{contestant.age || '-'}</TableCell>
                    <TableCell className="text-zinc-300">{contestant.hometown || '-'}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-zinc-400">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                          <DropdownMenuItem onClick={() => openEditDialog(contestant)} className="text-zinc-300">
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => { setDeletingContestant(contestant); setShowDeleteDialog(true); }}
                            className="text-red-400"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add/Edit Dialog */}
        <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
          if (!open) { setShowAddDialog(false); setShowEditDialog(false); setEditingContestant(null); resetForm(); }
        }}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {showEditDialog ? 'Edit Contestant' : 'Add Contestant'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Season Number *</Label>
                  <Input
                    type="number"
                    value={formData.season_number}
                    onChange={(e) => setFormData({ ...formData, season_number: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="e.g., 47"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Country *</Label>
                  <Select value={formData.country_id} onValueChange={(v) => setFormData({ ...formData, country_id: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      {countries.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  placeholder="Contestant name"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Age</Label>
                  <Input
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="Age"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Hometown</Label>
                  <Input
                    value={formData.hometown}
                    onChange={(e) => setFormData({ ...formData, hometown: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="City, State"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Photo</Label>
                <div className="flex gap-2 mb-2">
                  <Button
                    type="button"
                    variant={formData.photo_type === 'url' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFormData({ ...formData, photo_type: 'url' })}
                    className={formData.photo_type === 'url' ? 'bg-emerald-600' : 'border-zinc-700 text-zinc-400'}
                  >
                    URL
                  </Button>
                  <Button
                    type="button"
                    variant={formData.photo_type === 'upload' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFormData({ ...formData, photo_type: 'upload' })}
                    className={formData.photo_type === 'upload' ? 'bg-emerald-600' : 'border-zinc-700 text-zinc-400'}
                  >
                    Upload
                  </Button>
                  {formData.photo_url && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, photo_url: '' })}
                      className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {formData.photo_type === 'url' ? (
                  <Input
                    value={formData.photo_url}
                    onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="https://..."
                  />
                ) : (
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      try {
                        const uploadData = new FormData();
                        uploadData.append('file', file);
                        const res = await authAxios.post(`${API}/platform/default-contestants/upload-image`, uploadData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        setFormData({ ...formData, photo_url: res.data.url, photo_type: 'upload' });
                        toast.success('Image uploaded');
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Upload failed');
                      }
                    }}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                )}
                {formData.photo_url && (
                  <div className="mt-2">
                    <img
                      src={formData.photo_url}
                      alt="Preview"
                      className="w-16 h-16 rounded-lg object-cover"
                      onError={(e) => { 
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=Photo&background=3f3f46&color=fff&size=64`;
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAddDialog(false); setShowEditDialog(false); }} className="border-zinc-700">
                Cancel
              </Button>
              <Button
                onClick={showEditDialog ? handleEdit : handleAdd}
                disabled={processing}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : (showEditDialog ? 'Save' : 'Add')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Delete Contestant</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Are you sure you want to delete {deletingContestant?.name}? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleDelete} disabled={processing} variant="destructive">
                {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={showBulkUploadDialog} onOpenChange={setShowBulkUploadDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Bulk Upload Contestants</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Upload a CSV file with contestant data. Required columns: name. Optional: age, hometown, photo_url
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-300">Season Number *</Label>
                  <Input
                    type="number"
                    value={bulkSeasonNumber}
                    onChange={(e) => setBulkSeasonNumber(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="e.g., 47"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Country *</Label>
                  <Select value={bulkCountryId} onValueChange={setBulkCountryId}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-700">
                      {countries.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">CSV File *</Label>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setBulkFile(e.target.files[0])}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <Button variant="link" onClick={downloadTemplate} className="text-zinc-400 p-0 h-auto">
                <Download className="w-4 h-4 mr-1" /> Download CSV Template
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkUploadDialog(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button
                onClick={handleBulkUpload}
                disabled={processing || !bulkFile}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {processing ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage Countries Dialog */}
        <Dialog open={showCountriesDialog} onOpenChange={setShowCountriesDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Manage Countries</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Add or remove countries for the Survivor franchise
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  placeholder="Country name"
                  value={newCountry.name}
                  onChange={(e) => setNewCountry({ ...newCountry, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white flex-1"
                />
                <Input
                  placeholder="Code"
                  value={newCountry.code}
                  onChange={(e) => setNewCountry({ ...newCountry, code: e.target.value.toUpperCase() })}
                  className="bg-zinc-800 border-zinc-700 text-white w-20"
                  maxLength={3}
                />
                <Button onClick={handleAddCountry} size="sm" className="bg-emerald-600">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {countries.map(country => (
                  <div key={country.id} className="flex justify-between items-center p-2 bg-zinc-800 rounded">
                    <span className="text-white">
                      {country.name} <span className="text-zinc-500">({country.code})</span>
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteCountry(country.id)}
                      className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowCountriesDialog(false)} className="bg-zinc-700">
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminDefaultContestants;
